import { ErrorMessage, Field, Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { bindActionCreators } from 'redux';
import _actions from '../actions';
import DateTimeInfo from '../components/common/DateTimeInfo';
import DismissableAlert from '../components/common/DismissableAlert';
import ExpiredPackages from '../components/common/ExpiredPackages';
import { FormBackButton } from '../components/common/FormBackButton';
import { FormHeader } from '../components/common/FormHeader';
import { FormReset } from '../components/common/FormReset';
import { FormResetButton } from '../components/common/FormResetButton';
import { FormSubheader } from '../components/common/FormSubheader';
import { FormSubmit } from '../components/common/FormSubmit';
import { FormSubmitButton } from '../components/common/FormSubmitButton';
import { FormWrapper } from '../components/common/FormWrapper';
import PackageItem from '../components/package/PackageItem';
import emptySale from '../models/sale';
import availTimes from '../utils/availTimes';
import { getDay } from '../utils/date';
import daySkipped from '../utils/daySkipped';
import getMaxDate from '../utils/getMaxDate';
import packageKey from '../utils/packageKey';
import './Package.scss';

const PackageContainer = ({
  error,
  actions,
  loading,
  sale,
  inventory,
  config,
  done
}) => {
  useEffect(() => {
    if (!daySkipped(config) && (sale.id == null || sale.day == null)) {
      return actions.transitionTo('/');
    }

    if (daySkipped(config) && sale.day == null) {
      var maxDate = getMaxDate(config);

      actions.updateSale({
        day: getDay(maxDate || new Date())
      });
    }

    if (done) {
      return actions.transitionTo('/thanks');
    }

    actions.getPackages();
  }, []);

  const inventoryPkgs = inventory.packages[packageKey(sale.day)] || [];
  const hasInventory = inventoryPkgs.length > 0;

  return (
    <FormWrapper>
      <FormHeader>Buy Tickets: Select a Package</FormHeader>
      <FormSubheader>
        <DateTimeInfo date={sale.day} config={config} />
        <ExpiredPackages sale={sale} actions={actions} />
      </FormSubheader>
      {inventory.loading ? (
        'Loading'
      ) : hasInventory ? (
        <Formik
          initialValues={{
            packages: sale.packages
          }}
          validate={({ packages }) => {
            let errors = {};
            if (Object.keys(packages).length < 1) {
              errors.packages = 'Please select a package';
            }

            var hasQty = !!Object.keys(packages).filter(
              pkgId => packages[pkgId].qty != null && packages[pkgId].qty > 0
            ).length;

            inventoryPkgs.forEach(pkg => {
              const _availTimes = availTimes(pkg, sale);

              const salePkg = packages[pkg.item_id] || {};
              if (salePkg.qty > 0) {
                if (
                  _availTimes != null &&
                  Object.keys(_availTimes).length > 0 &&
                  (salePkg.time == null || salePkg.time.trim() == '')
                ) {
                  errors.packages = `${pkg.descrip} requires a time.`;
                }
              }
            });

            if (!hasQty) {
              errors.packages = 'Please select a package';
            }

            return errors;
          }}
          onSubmit={(values, { setSubmitting }) => {
            // remove 0-qty packages
            Object.keys(values.packages)
              .filter(
                pkgId =>
                  values.packages[pkgId].qty == null ||
                  values.packages[pkgId].qty < 1
              )
              .forEach(pkgId => {
                delete values.packages[pkgId];
              });

            actions.updateSale(values);

            var promise;
            if (sale.id == null) {
              promise = actions.startSale();
            } else {
              promise = new Promise(resolve => resolve(sale.id));
            }

            promise.then(saleId => {
              actions
                .reserve(saleId)
                .then(() => {
                  setSubmitting(false);
                  actions.transitionTo('/addons');
                })
                .catch(() => {
                  setSubmitting(false);
                });
            });
          }}
        >
          {({ isSubmitting, setFieldValue }) => {
            const disabled = isSubmitting || loading;
            return (
              <Form>
                <Field
                  name="packages"
                  render={({ field }) => {
                    return inventoryPkgs.map(pkg => {
                      return (
                        <PackageItem
                          key={pkg.item_id}
                          onChange={({
                            time,
                            price,
                            pretax,
                            tax,
                            qty,
                            limit
                          }) => {
                            const newFieldValue = {
                              ...field.value,
                              [pkg.item_id]: {
                                ...field.value[pkg.item_id],
                                time,
                                price,
                                pretax,
                                tax,
                                qty,
                                limit
                              }
                            };

                            if (qty == 0) {
                              delete newFieldValue[pkg.item_id];
                            }

                            let prevTime = field.value[pkg.item_id]
                              ? field.value[pkg.item_id].time
                              : null;

                            // if dynamic pricing is enabled and time has changed, pre-expand item to get
                            // dynamic price for the selected time
                            if (
                              config.retrieveDynamicPrices &&
                              time !== prevTime
                            ) {
                              actions.getItemExpanded(pkg);
                            }

                            setFieldValue(field.name, newFieldValue);
                          }}
                          value={field.value[pkg.item_id]}
                          pkg={pkg}
                          disabled={disabled}
                          sale={sale}
                          config={config}
                          loadingItemExpandedInLineObj={
                            inventory.loadingItemExpandedInLineObj
                          }
                        />
                      );
                    });
                  }}
                />
                <ErrorMessage
                  name="packages"
                  component="div"
                  className="error-message"
                />

                <FormSubmit>
                  <FormBackButton
                    disabled={disabled}
                    onClick={() => actions.transitionTo('/')}
                  >
                    Back
                  </FormBackButton>
                  <FormSubmitButton disabled={disabled}>
                    Continue
                    {disabled ? (
                      <span>
                        &nbsp;
                        <i className="fa fa-spinner fa-pulse" />
                      </span>
                    ) : (
                      ''
                    )}
                  </FormSubmitButton>
                </FormSubmit>
                <FormReset>
                  <FormResetButton
                    disabled={disabled}
                    onClick={() => {
                      actions.updateSale(emptySale());
                      actions.transitionTo('/');
                    }}
                  >
                    Start Over
                  </FormResetButton>
                </FormReset>
              </Form>
            );
          }}
        </Formik>
      ) : (
        'No inventory for that date'
      )}
      <DismissableAlert>{error || inventory.error}</DismissableAlert>
    </FormWrapper>
  );
};

PackageContainer.propTypes = {
  actions: PropTypes.object,
  params: PropTypes.object,
  router: PropTypes.object,
  route: PropTypes.object,
  error: PropTypes.string,
  inventory: PropTypes.object,
  loading: PropTypes.bool,
  sale: PropTypes.object,
  done: PropTypes.bool,
  config: PropTypes.object
};

const mapStateToProps = state => ({
  ...state.sale,
  config: state.config,
  inventory: state.inventory
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(_actions, dispatch)
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(PackageContainer));
